import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronDownIcon, 
  ChevronUpIcon, 
  XMarkIcon, 
  EnvelopeIcon, 
  IdentificationIcon, 
  CommandLineIcon,
  UserIcon
} from '@heroicons/react/24/outline';

// --- DATASET WITH EXTENDED TEAMS & METADATA ---
const orgData = {
  name: "Anjali Sharma",
  title: "Chief Executive Officer",
  role: "ceo",
  id: "EMP-001",
  email: "anjali.s@corp.com",
  skills: ["Strategy", "Leadership", "Governance"],
  children: [
    {
      name: "IT Services",
      title: "Division",
      role: "division",
      children: [
        {
          name: "App Dev",
          title: "Dept",
          role: "dept",
          children: [
            {
              name: "Siddharth Patel",
              title: "Delivery Manager",
              role: "manager",
              children: [
                {
                  name: "Priya Verma",
                  title: "Team Lead",
                  role: "tl",
                  children: [
                    { name: "Amit Singh", title: "Sr. Developer", role: "member", id: "IT-101", email: "amit.s@it.com", skills: ["React", "Node.js", "AWS"] },
                    { name: "Nisha Rao", title: "Developer", role: "member", id: "IT-102", email: "nisha.r@it.com", skills: ["Java", "Spring Boot"] },
                    { name: "Rahul Singh", title: "Frontend Dev", role: "member", id: "IT-103", email: "rahul.s@it.com", skills: ["Vue.js", "Tailwind"] },
                    { name: "Suresh Raina", title: "Backend Dev", role: "member", id: "IT-104", email: "suresh.r@it.com", skills: ["Python", "Django"] },
                    { name: "Ananya Roy", title: "UI Designer", role: "member", id: "IT-105", email: "ananya.r@it.com", skills: ["Figma", "UI/UX"] }
                  ]
                }
              ]
            }
          ]
        },
        {
          name: "QA Dept",
          title: "Dept",
          role: "dept",
          children: [
            {
              name: "Deepak J.",
              title: "QA Manager",
              role: "manager",
              children: [
                {
                  name: "Sonalika G.",
                  title: "QA Lead",
                  role: "tl",
                  children: [
                    { name: "Rahul V.", title: "QA Engineer", role: "member", id: "QA-201", email: "rahul.v@it.com", skills: ["Selenium", "Jira"] },
                    { name: "Suman K.", title: "Automation", role: "member", id: "QA-202", email: "suman.k@it.com", skills: ["Appium", "Python"] },
                    { name: "Pooja Hegde", title: "Manual Tester", role: "member", id: "QA-203", email: "pooja.h@it.com", skills: ["Black-box", "STLC"] }
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    {
      name: "Digital & Cloud",
      title: "Division",
      role: "division",
      children: [
        {
          name: "Cloud Ops",
          title: "Cloud Manager",
          role: "manager",
          children: [
            {
              name: "Arjun Mehta",
              title: "Cloud Lead",
              role: "tl",
              children: [
                { name: "Kiran P.", title: "Azure Spec.", role: "member", id: "CL-301", email: "kiran.p@cloud.com", skills: ["Azure", "Terraform"] },
                { name: "Samir L.", title: "Cloud Tech", role: "member", id: "CL-302", email: "samir.l@cloud.com", skills: ["AWS", "Docker"] },
                { name: "Riya Sen", title: "DevOps Eng", role: "member", id: "CL-303", email: "riya.s@cloud.com", skills: ["Kubernetes", "CI/CD"] },
                { name: "Deepak T.", title: "Site Reliability", role: "member", id: "CL-304", email: "deepak.t@cloud.com", skills: ["Prometheus", "Linux"] }
              ]
            }
          ]
        }
      ]
    },
    {
      name: "Infrastructure",
      title: "Division",
      role: "division",
      children: [
        {
          name: "System Admin",
          title: "Admin Head",
          role: "manager",
          children: [
            {
              name: "Rohan D.",
              title: "System Lead",
              role: "tl",
              children: [
                { name: "Aman X.", title: "IT Support", role: "member", id: "INF-401", email: "aman.x@corp.com", skills: ["Windows Server", "AD"] },
                { name: "Zoya Q.", title: "Helpdesk", role: "member", id: "INF-402", email: "zoya.q@corp.com", skills: ["Networking", "O365"] }
              ]
            }
          ]
        }
      ]
    },
    {
      name: "Corporate",
      title: "Division",
      role: "division",
      children: [
        {
          name: "HR Dept",
          title: "HR Manager",
          role: "manager",
          children: [
            {
              name: "Kavita Iyer",
              title: "Recruitment Lead",
              role: "tl",
              children: [
                { name: "Meera W.", title: "HR Executive", role: "member", id: "HR-501", email: "meera.w@corp.com", skills: ["Payroll"] },
                { name: "Sana K.", title: "Talent Scout", role: "member", id: "HR-502", email: "sana.k@corp.com", skills: ["Sourcing"] }
              ]
            }
          ]
        },
        {
          name: "Finance",
          title: "Finance Manager",
          role: "manager",
          children: [
            {
              name: "Rajesh K.",
              title: "Accounts Lead",
              role: "tl",
              children: [
                { name: "Vikram S.", title: "Accountant", role: "member", id: "FIN-601", email: "vikram.s@corp.com", skills: ["Taxation", "Audit"] }
              ]
            }
          ]
        }
      ]
    }
  ]
};

// --- PROFESSIONAL COLOR SCHEME ---
const getRoleStyles = (role) => {
  switch(role) {
    case "ceo": return "border-indigo-600 bg-indigo-50/60";
    case "division": return "border-emerald-600 bg-emerald-50/60";
    case "manager": return "border-teal-600 bg-teal-50/60";
    case "tl": return "border-purple-600 bg-purple-50/60";
    default: return "border-slate-400 bg-slate-50/50";
  }
};

const getRoleAccent = (role) => {
  switch(role) {
    case "ceo": return "text-indigo-800 bg-indigo-100";
    case "division": return "text-emerald-800 bg-emerald-100";
    case "manager": return "text-teal-800 bg-teal-100";
    case "tl": return "text-purple-800 bg-purple-100";
    default: return "text-slate-700 bg-slate-100";
  }
};

// --- MODAL COMPONENT (FIXED FOR VIEWPORT CENTERING) ---
const ProfileModal = ({ person, onClose }) => {
  if (!person) return null;

  const efficiency = Math.floor(Math.random() * 30) + 70;
  const performance = Math.floor(Math.random() * 20) + 80;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden"
      >
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 p-6 flex justify-between items-start">
          <div className="flex gap-4 items-center">
            <div className={`h-16 w-16 rounded-full flex items-center justify-center text-2xl font-bold ${getRoleAccent(person.role)} shadow-inner`}>
              {person.name.charAt(0)}
            </div>
            <div>
              <h3 className="text-xl font-bold text-white font-poppins">{person.name}</h3>
              <p className="text-sm text-slate-300 font-medium mt-1">{person.title}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors p-1">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {(person.id || person.email) && (
            <div className="grid grid-cols-1 gap-4">
              {person.id && (
                <div className="flex items-center gap-3">
                  <IdentificationIcon className="h-5 w-5 text-slate-500" />
                  <div>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Employee ID</p>
                    <p className="text-sm font-medium text-slate-900">{person.id}</p>
                  </div>
                </div>
              )}
              {person.email && (
                <div className="flex items-center gap-3">
                  <EnvelopeIcon className="h-5 w-5 text-slate-500" />
                  <div>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Email</p>
                    <p className="text-sm font-medium text-slate-900">{person.email}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="pt-4 border-t border-slate-200">
            <div className="flex items-center gap-2 mb-4">
              <CommandLineIcon className="h-5 w-5 text-slate-600" />
              <p className="text-sm font-bold text-slate-700 uppercase tracking-wider">Key Skills</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {(person.skills || ["Leadership", "Communication"]).map((s) => (
                <span key={s} className="bg-slate-800 text-white px-3 py-1.5 rounded text-xs font-bold tracking-wide">
                  {s}
                </span>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6 pt-4 border-t border-slate-200">
            <div className="flex flex-col items-center">
              <div className="relative w-12 h-12">
                <svg className="w-12 h-12 transform -rotate-90">
                  <circle cx="24" cy="24" r="20" stroke="#e2e8f0" strokeWidth="4" fill="none" />
                  <circle cx="24" cy="24" r="20" stroke="#6366f1" strokeWidth="4" fill="none" 
                    strokeDasharray={`${(efficiency / 100) * 125.6} 125.6`} 
                    className="transition-all duration-700" />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-indigo-700">
                  {efficiency}%
                </span>
              </div>
              <p className="text-xs text-slate-600 mt-2 font-medium">Efficiency</p>
            </div>

            <div className="flex flex-col items-center">
              <div className="w-full max-w-20 h-8 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-teal-600 rounded-full transition-all duration-700 flex items-center justify-end pr-1"
                  style={{ width: `${performance}%` }}
                >
                  <span className="text-[10px] font-bold text-white">{performance}%</span>
                </div>
              </div>
              <p className="text-xs text-slate-600 mt-2 font-medium">Performance</p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// --- RECURSIVE NODE COMPONENT ---
const TreeNode = ({ node, onSelectPerson }) => {
  const [isOpen, setIsOpen] = useState(node.role === 'ceo' || node.role === 'division');
  const hasChildren = node.children && node.children.length > 0;
  const themeClass = getRoleStyles(node.role);
  const accentClass = getRoleAccent(node.role);

  const handleCardClick = (e) => {
    e.stopPropagation();
    if (node.role !== 'ceo' && (node.id || node.email)) {
      onSelectPerson(node);
    } else if (hasChildren) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className="flex flex-col items-center">
      <div className="relative flex flex-col items-center">
        <motion.div
          whileHover={{ y: -2 }}
          onClick={handleCardClick}
          className={`relative z-10 w-[160px] h-[96px] p-3 rounded-lg border-2 ${themeClass} cursor-pointer transition-all flex flex-col items-center justify-center text-center shadow-sm`}
        >
          <div className={`h-10 w-10 rounded-full ${accentClass} flex items-center justify-center mb-2 shadow`}>
            <UserIcon className="h-6 w-6 text-inherit opacity-80" />
          </div>
          <h4 className="font-poppins text-sm font-bold leading-tight text-slate-900">
            {node.name}
          </h4>
          <p className="font-roboto text-xs font-medium text-slate-600 mt-1">
            {node.title}
          </p>

          {hasChildren && (
            <div className="absolute -bottom-3">
              {isOpen ? <ChevronUpIcon className="h-4 w-4 text-slate-700" /> : <ChevronDownIcon className="h-4 w-4 text-slate-700" />}
            </div>
          )}
        </motion.div>

        {isOpen && hasChildren && (
          <div className="w-px h-12 bg-slate-500 mt-4" />
        )}
      </div>

      <AnimatePresence>
        {isOpen && hasChildren && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="relative flex justify-center mt-4"
          >
            <div className="absolute top-0 left-1/2 right-1/2 h-px bg-slate-500 translate-x-[-50%] w-[calc(100%+4rem)]" />

            <div className="flex gap-10 md:gap-16">
              {node.children.map((child, index) => (
                <div key={index} className="relative flex flex-col items-center pt-8">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-8 bg-slate-500" />
                  <TreeNode node={child} onSelectPerson={onSelectPerson} />
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// --- LEFT ANALYTICS PANEL (FLOW PARTICIPANT) ---
const AnalyticsRail = () => {
  const roleDistribution = {
    "CEO": 1, "Division": 4, "Manager": 6, "Team Lead": 5, "Member": 16
  };

  const totalEmployees = Object.values(roleDistribution).reduce((a, b) => a + b, 0);

  return (
    <div className="hidden lg:block w-72 bg-white border-r border-slate-200 flex-shrink-0 p-6 h-full">
        <h3 className="text-lg font-bold text-slate-800 mb-6 font-poppins">Organization Insights</h3>

        <div className="mb-10">
          <p className="text-sm font-medium text-slate-600 mb-4">Role Distribution</p>
          <div className="relative w-40 h-40 mx-auto">
            <svg className="w-full h-full" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="40" fill="none" stroke="#e2e8f0" strokeWidth="12" />
              <circle cx="50" cy="50" r="40" fill="none" stroke="#4f46e5" strokeWidth="12"
                strokeDasharray="251.3" strokeDashoffset="0" />
              <circle cx="50" cy="50" r="40" fill="none" stroke="#059669" strokeWidth="12"
                strokeDasharray="251.3" strokeDashoffset="-62.8" />
              <circle cx="50" cy="50" r="40" fill="none" stroke="#0d9488" strokeWidth="12"
                strokeDasharray="251.3" strokeDashoffset="-125.7" />
              <circle cx="50" cy="50" r="40" fill="none" stroke="#7c3aed" strokeWidth="12"
                strokeDasharray="251.3" strokeDashoffset="-188.5" />
              <circle cx="50" cy="50" r="40" fill="none" stroke="#64748b" strokeWidth="12"
                strokeDasharray="251.3" strokeDashoffset="-251.3" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-bold text-slate-800">{totalEmployees}</span>
              <span className="text-xs text-slate-500">Total</span>
            </div>
          </div>
          <div className="mt-4 text-xs space-y-1">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-indigo-600" />
              <span>CEO: 1</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-emerald-600" />
              <span>Division: 4</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-teal-600" />
              <span>Manager: 6</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-purple-600" />
              <span>Team Lead: 5</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-slate-500" />
              <span>Member: 16</span>
            </div>
          </div>
        </div>

        <div>
          <p className="text-sm font-medium text-slate-600 mb-4">Headcount by Division</p>
          <div className="space-y-4">
            {[
              { name: "IT Services", count: 16, color: "bg-emerald-600" },
              { name: "Digital & Cloud", count: 8, color: "bg-teal-600" },
              { name: "Infrastructure", count: 4, color: "bg-blue-600" },
              { name: "Corporate", count: 6, color: "bg-purple-600" }
            ].map((item) => (
              <div key={item.name} className="flex items-center gap-3">
                <span className="text-xs font-medium w-28 text-slate-700 truncate">{item.name}</span>
                <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${item.color} rounded-full transition-all duration-800`}
                    style={{ width: `${(item.count / totalEmployees) * 100}%` }}
                  />
                </div>
                <span className="text-xs font-bold text-slate-700 w-8 text-right">{item.count}</span>
              </div>
            ))}
          </div>
        </div>
    </div>
  );
};

// --- MAIN PAGE ---
const OrganizationPage = () => {
  const [selectedPerson, setSelectedPerson] = useState(null);

  return (
    <div className="relative min-h-screen bg-gray-50 font-roboto overflow-auto custom-scrollbar">
      {/* Root Layout Flow: Rail and Content are siblings in a flex-row */}
      <div className="flex min-w-max min-h-screen">
        
        {/* Analytics Rail: Non-sticky, participates in flow */}
        <AnalyticsRail />

        {/* Main Workspace */}
        <div className="flex-1 flex flex-col">
          <main className="flex-grow py-12 px-24">
            <div className="flex justify-center">
              <TreeNode node={orgData} onSelectPerson={setSelectedPerson} />
            </div>
          </main>

          {/* Role Legend: Part of the vertical flow at the bottom */}
          <footer className="border-t border-slate-200 bg-white py-6 px-8">
            <div className="flex flex-wrap justify-center gap-8">
              <LegendItem label="CEO" color="border-indigo-600 bg-indigo-100" />
              <LegendItem label="Division Head" color="border-emerald-600 bg-emerald-100" />
              <LegendItem label="Department Manager" color="border-teal-600 bg-teal-100" />
              <LegendItem label="Team Lead" color="border-purple-600 bg-purple-100" />
              <LegendItem label="Team Member" color="border-slate-400 bg-slate-100" />
            </div>
          </footer>
        </div>
      </div>

      <AnimatePresence>
        {selectedPerson && <ProfileModal person={selectedPerson} onClose={() => setSelectedPerson(null)} />}
      </AnimatePresence>

      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        body { margin: 0; padding: 0; }
      `}} />
    </div>
  );
};

const LegendItem = ({ label, color }) => (
  <div className="flex items-center gap-3">
    <div className={`w-5 h-5 rounded border-2 ${color}`} />
    <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">{label}</span>
  </div>
);

export default OrganizationPage;